//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { ContentBlock } from 'draft-js';
import { HashtagDropdownTrigger } from '@components/molecules/smart-input/recommended-hashtags/HashtagDropdownTrigger';
import { MentionsSuggestionsDropdownTrigger } from '@components/molecules/smart-input/mentions/MentionsSuggestionsDropdownTrigger';

// Copied from draft-js plugin metion trigger strategy - https://github.com/draft-js-plugins/draft-js-plugins/blob/master/packages/mention/src/defaultRegExp.ts
const DEFAULT_REGEX =
  '[' +
  '\\w-' +
  // Latin-1 Supplement (letters only) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin-1_Supplement
  '\u00C0-\u00D6' +
  '\u00D8-\u00F6' +
  '\u00F8-\u00FF' +
  // Latin Extended-A (without deprecated character) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin_Extended-A
  '\u0100-\u0148' +
  '\u014A-\u017F' +
  // Cyrillic symbols: \u0410-\u044F - https://en.wikipedia.org/wiki/Cyrillic_script_in_Unicode
  '\u0410-\u044F' +
  // hiragana (japanese): \u3040-\u309F - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  '\u3040-\u309F' +
  // katakana (japanese): \u30A0-\u30FF - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  '\u30A0-\u30FF' +
  // For an advanced explaination about Hangul see https://github.com/draft-js-plugins/draft-js-plugins/pull/480#issuecomment-254055437
  // Hangul Jamo (korean): \u3130-\u318F - https://en.wikipedia.org/wiki/Korean_language_and_computers#Hangul_in_Unicode
  // Hangul Syllables (korean): \uAC00-\uD7A3 - https://en.wikipedia.org/wiki/Korean_language_and_computers#Hangul_in_Unicode
  '\u3130-\u318F' +
  '\uAC00-\uD7A3' +
  // common chinese symbols: \u4e00-\u9eff - http://stackoverflow.com/a/1366113/837709
  '\u4e00-\u9eff' +
  // Arabic https://en.wikipedia.org/wiki/Arabic_(Unicode_block)
  '\u0600-\u06ff' +
  // Vietnamese http://vietunicode.sourceforge.net/charset/
  '\u00C0-\u1EF9' +
  ']';

const HASHTAG_TRIGGER_CHAR = '#';
const HASHTAG_TRIGGER_REGEX = new RegExp(
  `(\\s|^)${HASHTAG_TRIGGER_CHAR}${DEFAULT_REGEX}*`,
  'g',
);

const MENTION_TRIGGER_CHAR = '@';
const MENTION_TRIGGER_REGEX = new RegExp(
  `(\\s|^)${MENTION_TRIGGER_CHAR}${DEFAULT_REGEX}*`,
  'g',
);

/**
 * Find triggers in content
 *
 * @param regex
 * @param contentBlock
 * @param callback
 */
export const findWithRegex = (
  regex: RegExp,
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
): void => {
  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + (matchArr[0]?.length ?? 0));
  }
};

export const hashtagTriggerStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
) => {
  findWithRegex(HASHTAG_TRIGGER_REGEX, contentBlock, callback);
};

export const mentionTriggerStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
) => {
  findWithRegex(MENTION_TRIGGER_REGEX, contentBlock, callback);
};

export const recommendedHashtagsDecorators = [
  {
    strategy: hashtagTriggerStrategy,
    component: HashtagDropdownTrigger,
  },
];

export const mentionsSuggestionsDecorators = [
  {
    strategy: mentionTriggerStrategy,
    component: MentionsSuggestionsDropdownTrigger,
  },
];
